import React, { useMemo } from 'react';
import { Check, Clock, Coins, Gift, Loader2, Package } from 'lucide-react';
import { cn } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';
import {
  formatPlanPrice,
  formatXuiPkgDuration,
  isTrialCatalogPlan,
  isQuickTestVisible,
  localizeCatalogPlanLabel,
  localizeConnectionsLabel,
  localizeDurationLabel,
} from '../lib/createLineFromPlan';
import { formatPlanCheckoutTitle } from '../lib/planContentProfile';
import PlanContentBadge from './PlanContentBadge';
import './ProvisionModal.css';

function xuiIdOf(plan) {
  return String(plan?.xui_package_id || plan?.package_id || '').trim();
}

export default function PlanPicker({
  plans = [],
  packages = [],
  selectedId,
  onChange,
  loading = false,
  filter = 'all',
  emptyHint,
  className,
  intent = 'client',
}) {
  const { t, locale } = useLanguage();

  const pkgById = useMemo(() => {
    const map = {};
    for (const p of packages || []) {
      const id = String(p?.id || p?.package_id || '').trim();
      if (id) map[id] = p;
    }
    return map;
  }, [packages]);

  const visible = useMemo(() => {
    const rows = (plans || []).filter((p) => {
      if (!xuiIdOf(p)) return false;
      if (intent === 'quick-test') return isQuickTestVisible(p);
      return !p.disabled;
    });
    if (filter === 'all') return rows;
    return rows.filter((plan) => {
      const pkg = pkgById[xuiIdOf(plan)];
      const trial = isTrialCatalogPlan(plan, pkg);
      return filter === 'trial' ? trial : !trial;
    });
  }, [plans, pkgById, filter, intent]);

  if (loading) {
    return (
      <div className={cn('flex justify-center items-center gap-2 py-10 text-sm text-muted-foreground', className)}>
        <Loader2 className="h-4 w-4 animate-spin" /> {t('plan.loading')}
      </div>
    );
  }

  if (!visible.length) {
    return (
      <div className={cn('rounded-xl border border-dashed border-border bg-muted/30 px-4 py-8 text-center', className)}>
        <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-xl bg-primary/12 text-primary">
          <Package className="h-5 w-5" />
        </div>
        <p className="text-sm font-semibold text-foreground">{t('plan.noneTitle')}</p>
        <p className="mx-auto mt-1 max-w-sm text-[12.5px] leading-relaxed text-muted-foreground">
          {emptyHint || t('plan.noneHint')}
        </p>
      </div>
    );
  }

  return (
    <div className={cn('plan-picker-grid', className)}>
      {visible.map((plan) => {
        const id = String(plan.id || '');
        const xui = xuiIdOf(plan);
        const pkg = pkgById[xui];
        const trial = isTrialCatalogPlan(plan, pkg);
        const selected = id === String(selectedId || '');
        const rawLabel = String(plan.label || plan.package_name || pkg?.package_name || `${t('cc.packageCats')} ${id}`).trim();
        const label = localizeCatalogPlanLabel(formatPlanCheckoutTitle(plan) || rawLabel, t);
        const quick = intent === 'quick-test';
        const duration = quick
          ? ''
          : localizeDurationLabel(plan.duration_label, t) || formatXuiPkgDuration(pkg, { trial, t });
        const price = formatPlanPrice(plan.price, locale);
        const screens = localizeConnectionsLabel(plan.connections_label, t);
        const credits = plan.credit_policy?.base_credits ?? (pkg?.credits_cost != null ? Number(pkg.credits_cost) : 1);

        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange?.(id)}
            aria-pressed={selected}
            className={cn('plan-picker-card', selected && 'is-on')}
          >
            {selected ? (
              <span className="absolute right-2.5 top-2.5 grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground">
                <Check className="h-3 w-3" strokeWidth={2.6} />
              </span>
            ) : null}
            <div className="relative flex items-start gap-2.5 pr-6">
              <div
                className={cn(
                  'grid h-9 w-9 shrink-0 place-items-center rounded-lg',
                  selected ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
                )}
              >
                {quick || trial ? <Gift className="h-4 w-4" /> : <Package className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-display text-[13.5px] font-semibold leading-snug tracking-tight text-foreground line-clamp-2">
                  {label}
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {quick || trial ? (
                    <span className="rounded-md bg-emerald-500/12 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                      {t('plan.badgeTest')}
                    </span>
                  ) : (
                    <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                      {t('cc.client')}
                    </span>
                  )}
                  <PlanContentBadge plan={plan} />
                </div>
              </div>
            </div>
            <div className="plan-picker-card-foot flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px]">
              {duration ? (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {duration}
                </span>
              ) : null}
              {price ? (
                <span className="font-semibold tabular-nums text-foreground">{price}</span>
              ) : null}
              {screens ? <span className="text-muted-foreground">{screens}</span> : null}
              {!quick && !trial ? (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Coins className="h-3 w-3" />
                  {t('plan.credits', { n: Number.isFinite(credits) ? credits : 1 })}
                </span>
              ) : (
                <span className="text-muted-foreground">{t('plan.xuiPkg', { id: xui })}</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
